import { LayoutGrid, List } from 'lucide-react';
import { useState } from 'react';
import type { InvestmentAccountReturns, InvestmentPortfolioResponse } from 'shared';

import { formatCurrency } from '../../lib/format/currency';
import { useUiStore } from '../../stores/uiStore';
import { Button } from '../ui/Button';

import { InvestmentHoldingsTable } from './InvestmentHoldingsTable';
import { InvestmentReturnsPanel } from './InvestmentReturnsPanel';
import { InvestmentThemeEditor } from './InvestmentThemeEditor';

function money(value: string | null, currency: string) {
  return value === null ? 'Unavailable' : formatCurrency(value, currency);
}

function pct(value: string | null) {
  return value === null ? 'Unavailable' : `${Number(value).toFixed(1)}%`;
}

type CurrencyPortfolio = InvestmentPortfolioResponse['currencies'][number];
type Holding = CurrencyPortfolio['holdings'][number];

function holdingReturnText(holding: Holding, currency: string): string {
  if (holding.totalReturn === null) {
    const unexplained = holding.accounts
      .filter((account) => account.unexplainedUnits !== null)
      .map((account) => `${account.unexplainedUnits} units in ${account.accountName}`);
    return unexplained.length > 0
      ? `History incomplete: trades don't explain ${unexplained.join(', ')}`
      : 'History incomplete';
  }
  return `${money(holding.totalReturn, currency)} / ${pct(holding.totalReturnPct)}`;
}

export function InvestmentCurrencyPortfolio({
  portfolio,
  accountReturns,
  onSelectAccount,
}: {
  portfolio: CurrencyPortfolio;
  accountReturns: readonly InvestmentAccountReturns[];
  onSelectAccount: (accountId: string) => void;
}) {
  const [themeFilter, setThemeFilter] = useState<string | null | undefined>(undefined);
  const holdingsLayout = useUiStore((state) => state.holdingsLayout);
  const setHoldingsLayout = useUiStore((state) => state.setHoldingsLayout);
  const holdings =
    themeFilter === undefined
      ? portfolio.holdings
      : portfolio.holdings.filter((holding) => holding.theme === themeFilter);
  return (
    <div className="space-y-5">
      <dl className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ['Portfolio value', money(portfolio.accountValue, portfolio.currency)],
          ['Invested value', money(portfolio.holdingsValue, portfolio.currency)],
          [
            'Cash',
            `${money(portfolio.cash, portfolio.currency)} (${pct(portfolio.cashWeightPct)})`,
          ],
          [
            'Unrealised P&L',
            `${money(portfolio.unrealizedPnl, portfolio.currency)} (${pct(portfolio.unrealizedPnlPct)})`,
          ],
        ].map(([label, value]) => (
          <div key={label} className="min-w-0 rounded border border-border bg-bg p-4">
            <dt className="text-sm text-text-muted">{label}</dt>
            <dd className="mt-2 break-words text-xl font-semibold tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="text-sm text-text-muted">
        {portfolio.holdings.length} distinct investments across {portfolio.accountCount} valued
        accounts. Top five holdings: {pct(portfolio.topFiveWeightPct)} of portfolio value. Weights
        include cash in the denominator. Unrealised P&amp;L is value minus imported cost; see
        Returns below for gains that include realised profits, dividends, interest and fees.
      </p>
      <InvestmentReturnsPanel
        returns={portfolio.returns}
        accounts={accountReturns.filter((account) => account.currency === portfolio.currency)}
      />
      <section aria-label="Theme exposure" className="space-y-3">
        <h3 className="font-semibold">Theme exposure</h3>
        <p className="text-sm text-text-muted">
          One confirmed primary theme per investment, shared across long-term accounts. Suggestions
          do not count as classified exposure. Funds are not analysed through to their underlying
          holdings.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {portfolio.themes.map((theme) => (
            <button
              type="button"
              key={JSON.stringify(theme.theme)}
              aria-pressed={themeFilter === theme.theme}
              onClick={() => setThemeFilter(themeFilter === theme.theme ? undefined : theme.theme)}
              className="min-w-0 rounded border border-border bg-bg p-4 text-left focus-visible:outline focus-visible:outline-primary"
            >
              <div className="flex flex-wrap justify-between gap-2 font-medium">
                <span className="break-words">{theme.theme ?? 'Unclassified'}</span>
                <span className="tabular-nums">{pct(theme.weightPct)}</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded bg-bg-muted" aria-hidden="true">
                <div
                  className="h-full bg-primary"
                  style={{ width: `${Math.min(100, Math.max(0, Number(theme.weightPct ?? 0)))}%` }}
                />
              </div>
              <p className="mt-2 text-sm tabular-nums text-text-muted">
                {money(theme.value, portfolio.currency)} · {theme.holdingCount} investments
              </p>
              <p className="mt-1 text-sm tabular-nums text-text-muted">
                P&amp;L {money(theme.unrealizedPnl, portfolio.currency)} (
                {pct(theme.unrealizedPnlPct)})
              </p>
              <p className="mt-1 text-sm tabular-nums text-text-muted">
                {theme.totalReturn === null
                  ? 'Total return unavailable: history incomplete'
                  : `Total return ${money(theme.totalReturn, portfolio.currency)} (${pct(theme.totalReturnPct)})`}
                {theme.totalReturn !== null &&
                theme.returnReconciledCount < theme.returnHoldingCount
                  ? ` · ${theme.returnReconciledCount} of ${theme.returnHoldingCount} positions`
                  : ''}
              </p>
            </button>
          ))}
        </div>
      </section>
      <section aria-label="Aggregate long-term holdings" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold">
            Combined holdings
            {themeFilter !== undefined ? ` - ${themeFilter ?? 'Unclassified'}` : ''}
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            {themeFilter !== undefined ? (
              <Button size="sm" variant="ghost" onClick={() => setThemeFilter(undefined)}>
                Show all themes
              </Button>
            ) : null}
            <div
              className="inline-flex rounded border border-border p-0.5"
              role="group"
              aria-label="Holdings layout"
            >
              {(
                [
                  { value: 'cards', label: 'Cards', icon: LayoutGrid },
                  { value: 'list', label: 'List', icon: List },
                ] as const
              ).map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={holdingsLayout === value}
                  onClick={() => setHoldingsLayout(value)}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-text-muted aria-pressed:bg-surface-raised aria-pressed:text-text"
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {holdings.length === 0 ? (
          <p className="text-sm text-text-muted">No holdings in this view.</p>
        ) : null}
        {holdingsLayout === 'list' && holdings.length > 0 ? (
          <InvestmentHoldingsTable
            holdings={holdings}
            currency={portfolio.currency}
            onSelectAccount={onSelectAccount}
          />
        ) : null}
        {holdingsLayout === 'list' ? null : (
          <div className="grid gap-3 lg:grid-cols-2">
            {holdings.map((holding) => (
              <article
                key={holding.identity}
                className="min-w-0 space-y-3 rounded border border-border p-4"
              >
                <div>
                  <h4 className="font-semibold">{holding.symbol}</h4>
                  <p className="break-words text-sm text-text-muted">{holding.stock}</p>
                </div>
                <dl className="grid grid-cols-2 gap-3 text-sm tabular-nums">
                  <div>
                    <dt className="text-text-muted">Value / weight</dt>
                    <dd>
                      {money(holding.value, portfolio.currency)} / {pct(holding.weightPct)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-text-muted">Cost basis</dt>
                    <dd>{money(holding.cost, portfolio.currency)}</dd>
                  </div>
                  <div>
                    <dt className="text-text-muted">Unrealised P&amp;L</dt>
                    <dd>
                      {money(holding.unrealizedPnl, portfolio.currency)} /{' '}
                      {pct(holding.unrealizedPnlPct)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-text-muted">Accounts held</dt>
                    <dd>{holding.accounts.length}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-text-muted">
                      Total return (incl. realised gains and dividends)
                    </dt>
                    <dd className={holding.totalReturn === null ? 'text-text-muted' : undefined}>
                      {holdingReturnText(holding, portfolio.currency)}
                    </dd>
                    {holding.totalReturn !== null ? (
                      <dd className="text-xs text-text-muted">
                        Realised {money(holding.realisedPnl, portfolio.currency)} · dividends{' '}
                        {money(holding.income, portfolio.currency)} · on{' '}
                        {money(holding.invested, portfolio.currency)} bought in total
                      </dd>
                    ) : null}
                  </div>
                </dl>
                <InvestmentThemeEditor
                  key={`${holding.identity}:${holding.theme ?? ''}`}
                  holding={holding}
                />
                <ul
                  className="space-y-1 text-sm text-warn"
                  aria-label={`Review signals for ${holding.symbol}`}
                >
                  {holding.missingThesisCount > 0 ? (
                    <li>Missing or unconfirmed thesis in {holding.missingThesisCount} accounts</li>
                  ) : null}
                  {holding.reviewDueCount > 0 ? (
                    <li>Thesis review due in {holding.reviewDueCount} accounts</li>
                  ) : null}
                  {holding.brokenThesisCount > 0 ? (
                    <li>Broken thesis in {holding.brokenThesisCount} accounts</li>
                  ) : null}
                  {holding.aboveMaximumCount > 0 ? (
                    <li>Above your allocation maximum in {holding.aboveMaximumCount} accounts</li>
                  ) : null}
                </ul>
                <div className="border-t border-border pt-2">
                  <p className="text-xs text-text-muted">
                    Account breakdown - select to manage thesis and allocation
                  </p>
                  {holding.accounts.map((account) => (
                    <button
                      key={account.holdingId}
                      type="button"
                      onClick={() => onSelectAccount(account.accountId)}
                      className="mt-1 block w-full break-words text-left text-sm text-text underline hover:decoration-primary"
                    >
                      {account.accountName}: {money(account.value, portfolio.currency)}
                      {account.units === null ? '' : ` · ${account.units} units`}
                    </button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
